import { useEffect, useMemo, useRef, useState } from 'react';
import { getErrorMessage } from '@/services/apiClient';
import {
  createSubscribeIntent,
  saveSubscribePaymentMethod,
  type SubscribeBillingFacility,
  type SubscribeBillingIntent,
  type SubscribeBillingPaymentMethod,
} from '@/services/billingAdmin.service';

type ForwardPaymentElement = {
  mount: (selectorOrElement: string | HTMLElement, options: Record<string, unknown>) => void;
  unmount?: () => void;
  destroy?: () => void;
};

type ForwardWindow = Window & {
  Forward?: {
    createPaymentElement: (config: { apiKey: string; clientSecret: string }) => Promise<ForwardPaymentElement>;
  };
};

interface SubscribeBillingMethodCaptureProps {
  active: boolean;
  facilities: SubscribeBillingFacility[];
  onSaved: (method: SubscribeBillingPaymentMethod) => void;
  onCancel: () => void;
}

/**
 * Forward SDK mount for the admin "Account > Billing" page. Sibling of
 * `ForwardBillingMethodCapture` but talks to the subscribe billing
 * endpoints (subscribe_merchants / subscribe_payment_methods) instead of
 * the patient billing endpoints (aa_accounts_forward /
 * patients_payment_methods_authnet).
 *
 * Adds two inputs above the Forward dropin — billing name and facility —
 * matching the legacy `admin_billing_list.php` form.
 */
export function SubscribeBillingMethodCapture({
  active,
  facilities,
  onSaved,
  onCancel,
}: SubscribeBillingMethodCaptureProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const paymentElementRef = useRef<ForwardPaymentElement | null>(null);
  const [intent, setIntent] = useState<SubscribeBillingIntent | null>(null);
  const [loadingIntent, setLoadingIntent] = useState(false);
  const [savingMethod, setSavingMethod] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [billingName, setBillingName] = useState('');
  const [facilityID, setFacilityID] = useState<number>(0);

  // Stash form values in refs so the Forward onSuccess callback (closed
  // over the initial render) always sees the latest user input.
  const billingNameRef = useRef(billingName);
  const facilityIDRef = useRef(facilityID);
  useEffect(() => { billingNameRef.current = billingName; }, [billingName]);
  useEffect(() => { facilityIDRef.current = facilityID; }, [facilityID]);

  useEffect(() => {
    if (!active) {
      setIntent(null);
      setError(null);
      setNotice(null);
      return;
    }

    let cancelled = false;
    setLoadingIntent(true);
    setError(null);
    setNotice(null);

    createSubscribeIntent()
      .then((result) => {
        if (!cancelled) setIntent(result);
      })
      .catch((err) => {
        if (!cancelled) setError(getErrorMessage(err, 'Unable to prepare the Forward card form.'));
      })
      .finally(() => {
        if (!cancelled) setLoadingIntent(false);
      });

    return () => { cancelled = true; };
  }, [active]);

  useEffect(() => {
    if (!active || !intent || !containerRef.current) return;
    if (!intent.public_api_key || !intent.client_secret) {
      setError('Forward did not return the required card-form credentials.');
      return;
    }

    const forwardWindow = window as ForwardWindow;
    if (!forwardWindow.Forward?.createPaymentElement) {
      setError('Forward.js is not available in this session.');
      return;
    }

    let cancelled = false;
    let expMonth = '';
    let expYear = '';
    let lastFour = '';
    let cardType = '';

    forwardWindow.Forward.createPaymentElement({
      apiKey: intent.public_api_key,
      clientSecret: intent.client_secret,
    })
      .then((paymentElement) => {
        if (cancelled) return;
        paymentElementRef.current = paymentElement;
        paymentElement.mount(containerRef.current as HTMLElement, {
          hidePostalCode: true,
          hideCountry: true,
          onChange: (event: { ready?: boolean; card?: { expMonth?: string; expYear?: string; brand?: string; lastFourDigits?: string } }) => {
            if (event.ready) {
              expMonth = event.card?.expMonth ?? '';
              expYear = event.card?.expYear ?? '';
              lastFour = event.card?.lastFourDigits ?? '';
              cardType = event.card?.brand ?? '';
            }
          },
          onReady: async (tokenize: () => Promise<void>) => {
            await tokenize();
          },
          onSuccess: async ({ token }: { token: string }) => {
            if (cancelled) return;
            setSavingMethod(true);
            setError(null);
            try {
              const saved = await saveSubscribePaymentMethod({
                token,
                intent_id: intent.intent_id ?? undefined,
                billing_name: billingNameRef.current.trim() || undefined,
                facilityID: facilityIDRef.current || 0,
                cardType: cardType || undefined,
                lastfour: lastFour || undefined,
                exp_month: expMonth ? Number(expMonth) : undefined,
                exp_year: expYear ? Number(expYear) : undefined,
              });
              setNotice(`Saved ${saved.brand || 'card'} •••• ${saved.last4 || ''}`);
              onSaved(saved);
            } catch (err) {
              setError(getErrorMessage(err, 'Failed to save the new billing method.'));
            } finally {
              setSavingMethod(false);
            }
          },
          onError: (err: unknown) => {
            if (!cancelled) setError(getErrorMessage(err, 'Forward card entry failed.'));
          },
        });
      })
      .catch((err) => {
        if (!cancelled) setError(getErrorMessage(err, 'Unable to mount the Forward card form.'));
      });

    return () => {
      cancelled = true;
      const paymentElement = paymentElementRef.current;
      if (paymentElement?.destroy) paymentElement.destroy();
      else if (paymentElement?.unmount) paymentElement.unmount();
      paymentElementRef.current = null;
      if (containerRef.current) containerRef.current.innerHTML = '';
    };
  }, [intent, active, onSaved]);

  const helperText = useMemo(() => {
    if (loadingIntent) return 'Preparing secure card entry...';
    if (savingMethod) return 'Saving the new billing method...';
    return '';
  }, [loadingIntent, savingMethod]);

  if (!active) return null;

  return (
    <div className="space-y-3 rounded-lg border-2 border-dashed border-indigo-300 bg-indigo-50 p-4">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-indigo-900">New Billing Method</div>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs font-medium text-indigo-700 hover:text-indigo-900 underline"
        >
          Cancel
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <label className="block text-xs">
          <span className="mb-1 block font-medium text-slate-700">Billing Name (optional)</span>
          <input
            type="text"
            value={billingName}
            onChange={(e) => setBillingName(e.target.value)}
            placeholder="Cardholder name"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </label>

        <label className="block text-xs">
          <span className="mb-1 block font-medium text-slate-700">Facility (optional)</span>
          <select
            value={facilityID}
            onChange={(e) => setFacilityID(Number(e.target.value))}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value={0}>— No Facility —</option>
            {facilities.map((f) => (
              <option key={f.facilityID} value={f.facilityID}>{f.name}</option>
            ))}
          </select>
        </label>
      </div>

      {helperText ? <p className="text-xs text-indigo-800">{helperText}</p> : null}
      {notice ? <p className="text-xs text-emerald-700">{notice}</p> : null}
      {error ? <p className="text-xs text-red-700">{error}</p> : null}

      <div className="text-xs font-medium text-slate-700">Payment Information</div>
      <div
        ref={containerRef}
        className="min-h-[140px] rounded-md border border-slate-300 bg-white p-4"
      />
    </div>
  );
}
